// Resolves the active entry-multiplier promotion from the OC API and mirrors it
// onto the cart as a cart attribute, so Checkout Blocks and Flow can see it.
//
// Cached promos carry their own startDate/endDate and the window is evaluated at
// READ time, not fetch time. A stale cache therefore can never resurrect a promo
// that has already ended — the TTL only governs how quickly a *new* promo appears.
//
// This is display/reporting only. The Orders API independently re-derives the
// multiplier from the promotion window vs. the order's processedAt, so a missed
// attribute costs a badge, never an entry count.
//
// Public API:
//   getPromotion()     -> the live promo object, or null
//   getMultiplier()     -> number, 1 when no promo is live
//   isActive()          -> boolean
//   getTimeRemaining()  -> {total, days, hours, minutes, seconds} or null
//   syncCartAttribute() -> Promise
//   ready()             -> Promise, resolves once any in-flight fetch settles
window.OCEntryMultiplier = (function () {
  const ENDPOINT = "https://api.onecountry.com/v2/promotions";
  const STORAGE_KEY = "oc_promotions";
  // Same identifier on both sides of the boundary: the promotion's `tag` in the
  // OC API and the Shopify cart attribute key read by Checkout Blocks and Flow.
  const PROMO_KEY = "entry_multiplier";
  // Only bounds how fast a *new* or *edited* promo propagates — expiry is handled
  // at read time by isLive(), so this is not a staleness guard. A promo record
  // created ahead of its startDate needs no refetch at all to switch on.
  const TTL_MS = 10 * 60 * 1000;
  const FETCH_TIMEOUT_MS = 1500;

  // Local preview only — MUST be null in production. When set, fetchPromotions()
  // short-circuits before the network call, so the real promotion record (and
  // anything on it, such as excludedSkus) never reaches the browser.
  //
  // To preview, set to an array of promo objects, e.g.:
  //   [{ tag: "entry_multiplier", status: "active",
  //      startDate: new Date(Date.now() - 86400000).toISOString(),
  //      endDate:   new Date(Date.now() + 172800000).toISOString(),
  //      attributes: { multiplier: 2, excludedSkus: [] } }]
  //
  // It writes through localStorage exactly like a real response, so the TTL
  // applies. After changing it, run:
  //   localStorage.removeItem('oc_promotions')
  const STUB_PROMOTIONS = null;

  let memoryCache = null; // fallback when localStorage is unavailable
  let inFlight = null;

  function readCache() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) {
      // private mode, quota, or malformed JSON — fall through to memory
    }
    return memoryCache;
  }

  function writeCache(payload) {
    memoryCache = payload;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      // memoryCache still serves this pageload
    }
  }

  function isLive(promo) {
    const now = Date.now();
    if (promo.startDate && now < new Date(promo.startDate).getTime()) return false;
    if (promo.endDate && now > new Date(promo.endDate).getTime()) return false;
    return true;
  }

  function fetchPromotions() {
    if (inFlight) return inFlight;
    // Preview short-circuit. Writes through the cache like a real response so
    // every downstream read exercises its normal path. No-op when null.
    if (STUB_PROMOTIONS) {
      writeCache({ fetchedAt: Date.now(), promotions: STUB_PROMOTIONS });
      return Promise.resolve(STUB_PROMOTIONS);
    }
    const controller = new AbortController();
    const timer = setTimeout(function () {
      controller.abort();
    }, FETCH_TIMEOUT_MS);
    inFlight = fetch(ENDPOINT, { signal: controller.signal })
      .then(function (res) {
        return res.ok ? res.json() : Promise.reject(res.status);
      })
      .then(function (promotions) {
        writeCache({ fetchedAt: Date.now(), promotions: promotions || [] });
        return promotions;
      })
      .catch(function () {
        return null; // a promo lookup must never break a purchase
      })
      .finally(function () {
        clearTimeout(timer);
        inFlight = null;
      });
    return inFlight;
  }

  // Synchronous — safe to call from a click handler, a render loop, or a tick.
  function getPromotion() {
    const cache = readCache();
    if (!cache || !cache.promotions) return null;
    return (
      cache.promotions.find(function (p) {
        return p.tag === PROMO_KEY && isLive(p);
      }) || null
    );
  }

  function getMultiplier() {
    const promo = getPromotion();
    return (promo && promo.attributes && promo.attributes.multiplier) || 1;
  }

  function isActive() {
    return getMultiplier() > 1;
  }

  // SKUs the promotion never multiplies. Served with the promo rather than
  // hardcoded, so this list stays in step with the trigger without a deploy.
  function getExcludedSkus() {
    const promo = getPromotion();
    const skus = promo && promo.attributes && promo.attributes.excludedSkus;
    return Array.isArray(skus) ? skus : [];
  }

  // Trimmed and lowercased on both sides. The list is hand-entered into Mongo and
  // the SKU comes from Liquid, so an exact === would fail silently on a stray
  // space or a capital — and the failure mode is the cart promising a multiplier
  // the trigger won't honour.
  function normalizeSku(sku) {
    return String(sku == null ? "" : sku).trim().toLowerCase();
  }

  // Per-line-item multiplier: 1 for excluded SKUs, the promo value otherwise.
  function getMultiplierForSku(sku) {
    const normalized = normalizeSku(sku);
    if (!normalized) return getMultiplier();
    const excluded = getExcludedSkus().map(normalizeSku);
    return excluded.indexOf(normalized) !== -1 ? 1 : getMultiplier();
  }

  // Components, not a formatted string: a promo can run longer than 24h, and
  // HH:MM:SS slicing of a duration silently wraps. Caller decides the copy.
  function getTimeRemaining() {
    const promo = getPromotion();
    if (!promo || !promo.endDate) return null;
    const total = new Date(promo.endDate).getTime() - Date.now();
    if (total <= 0) return null;
    return {
      total: total,
      days: Math.floor(total / (1000 * 60 * 60 * 24)),
      hours: Math.floor((total / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((total / (1000 * 60)) % 60),
      seconds: Math.floor((total / 1000) % 60),
    };
  }

  // Writes the cart attribute only when it differs from what Liquid rendered, so a
  // normal pageload costs zero extra requests. An empty string REMOVES the
  // attribute — that is what expires the checkout messaging when a promo ends.
  function syncCartAttribute() {
    const desired = isActive() ? String(getMultiplier()) : "";
    const current = (window.OCCart && window.OCCart.entryMultiplier) || "";
    if (desired === current) return Promise.resolve();

    const attributes = {};
    attributes[PROMO_KEY] = desired;
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: attributes }),
    })
      .then(function () {
        if (window.OCCart) window.OCCart.entryMultiplier = desired;
      })
      .catch(function () {
        return null; // never let this break a pageload
      });
  }

  // --- display ---------------------------------------------------------------

  // Only the multiplier itself is written here. The kicker copy ("Entries" vs
  // "First Month") is rendered by the snippet from its `kind`, because a flat
  // "2X" next to a monthly count over-promises — only the first month is
  // doubled. Keeping that copy in Liquid puts it where it can be edited without
  // touching JS.
  function renderFlashes() {
    const multiplier = getMultiplier();
    const nodes = document.querySelectorAll("[data-entry-multiplier-flash]");
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (multiplier > 1) {
        const mult = node.querySelector("[data-em-mult]");
        if (mult) mult.textContent = multiplier;
        node.style.display = "";
      } else {
        node.style.display = "none";
      }
    }
  }

  function formatCountdown(remaining) {
    const pad = function (n) {
      return n < 10 ? "0" + n : String(n);
    };
    // Days are kept separate rather than folded into hours — a duration past 24h
    // formatted as HH:MM:SS silently wraps.
    if (remaining.days > 0) {
      return remaining.days + "d " + pad(remaining.hours) + "h " + pad(remaining.minutes) + "m";
    }
    return pad(remaining.hours) + "h " + pad(remaining.minutes) + "m " + pad(remaining.seconds) + "s";
  }

  let barTimer = null;
  let barResizeBound = false;

  // Builds the ticker: enough copies of the message to exceed the bar width, then
  // that whole set duplicated so translateX(-50%) lands on an identical frame.
  // Called on reveal and on resize — NOT on every countdown tick, which would
  // restart the animation once a second.
  function buildMarquee(bar, message) {
    const track = bar.querySelector(".promo-attention-bar__track");
    if (!track) return;
    const scrolling = bar.getAttribute("data-scroll-mode") === "scroll";
    const gap = parseFloat(bar.getAttribute("data-gap")) || 3;
    bar.style.setProperty("--promo-gap", gap + "rem");

    const makeCopy = function (hidden) {
      const span = document.createElement("span");
      span.className = "promo-attention-bar__message";
      span.textContent = message;
      if (hidden) span.setAttribute("aria-hidden", "true");
      return span;
    };

    track.textContent = "";
    track.appendChild(makeCopy(false));

    if (!scrolling) {
      track.style.animationDuration = "";
      return;
    }

    // Measure one copy (includes its trailing gap) against the bar.
    const barWidth = bar.clientWidth;
    const copyWidth = track.firstChild.offsetWidth;
    if (!copyWidth || !barWidth) return;

    const perHalf = Math.max(1, Math.ceil(barWidth / copyWidth));
    for (let i = 1; i < perHalf * 2; i++) {
      track.appendChild(makeCopy(true));
    }

    // Constant px/sec: one cycle travels half the track, i.e. perHalf copies.
    const pxPerSec = parseFloat(bar.getAttribute("data-scroll-speed")) || 60;
    track.style.animationDuration = (perHalf * copyWidth) / pxPerSec + "s";
  }

  function currentMessage(bar, multiplier) {
    const template = bar.getAttribute("data-text") || "";
    let message = template.replace(/\{\{\s*multiplier\s*\}\}/g, multiplier);
    if (bar.getAttribute("data-show-countdown") === "true") {
      const remaining = getTimeRemaining();
      if (!remaining) return null; // window closed while the page was open
      const prefix = bar.getAttribute("data-countdown-prefix") || "";
      message += "  ·  " + (prefix ? prefix + " " : "") + formatCountdown(remaining);
    }
    return message;
  }

  function renderBar() {
    const bar = document.getElementById("promo-attention-bar");
    if (!bar) return;
    const multiplier = getMultiplier();

    if (multiplier <= 1) {
      bar.style.display = "none";
      if (barTimer) {
        clearInterval(barTimer);
        barTimer = null;
      }
      // Re-run the theme's fixed-header math so the header closes the gap.
      window.dispatchEvent(new Event("resize"));
      return;
    }

    const message = currentMessage(bar, multiplier);
    if (message === null) {
      // Promo ended with the page open — drop the bar rather than leaving a
      // stale promise on screen.
      bar.style.display = "none";
      if (barTimer) {
        clearInterval(barTimer);
        barTimer = null;
      }
      window.dispatchEvent(new Event("resize"));
      return;
    }

    // Must be visible before measuring — offsetWidth is 0 while display:none.
    bar.style.display = "";
    buildMarquee(bar, message);
    // The bar has height now, so the header and main offsets need recomputing.
    window.dispatchEvent(new Event("resize"));

    if (bar.getAttribute("data-show-countdown") === "true" && !barTimer) {
      barTimer = setInterval(function () {
        const next = currentMessage(bar, getMultiplier());
        if (next === null) {
          renderBar();
          return;
        }
        // Text only — rebuilding here would restart the scroll every second.
        const copies = bar.querySelectorAll(".promo-attention-bar__message");
        for (let i = 0; i < copies.length; i++) copies[i].textContent = next;
      }, 1000);
    }

    if (!barResizeBound) {
      barResizeBound = true;
      let resizeTimer = null;
      window.addEventListener("resize", function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          if (getMultiplier() <= 1) return;
          const text = currentMessage(bar, getMultiplier());
          if (text !== null) buildMarquee(bar, text);
        }, 200);
      });
    }
  }

  function render() {
    renderFlashes();
    renderBar();
  }

  // --- init -------------------------------------------------------------------

  const cached = readCache();
  syncCartAttribute(); // act on whatever cache we have, immediately
  if (!cached || Date.now() - cached.fetchedAt > TTL_MS) {
    fetchPromotions().then(function () {
      syncCartAttribute(); // re-sync if it changed
      render(); // and repaint, in case this was a cold cache
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", render);
  } else {
    render();
  }

  return {
    render: render,
    getPromotion: getPromotion,
    getMultiplier: getMultiplier,
    getMultiplierForSku: getMultiplierForSku,
    getExcludedSkus: getExcludedSkus,
    isActive: isActive,
    getTimeRemaining: getTimeRemaining,
    syncCartAttribute: syncCartAttribute,
    ready: function () {
      return inFlight || Promise.resolve();
    },
  };
})();
